import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { FUN_FACTS } from "@/api/queries/funFacts";

export interface FunFact {
  question: string;
  answer: string;
  cursor: string;
}

export interface UseFunFactsReturn {
  funFacts: FunFact[];
  loading: boolean;
  error: unknown;
  totalCount: number;
  pageInfo: {
    startCursor?: string | null;
    endCursor?: string | null;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  getFunFactByQuestion: (questionText: string) => FunFact | null;
  hasFunFacts: boolean;
}

export const useFunFacts = (
  studentId?: string,
  options?: {
    first?: number;
    last?: number;
    after?: string;
    before?: string;
  },
): UseFunFactsReturn => {
  const { data, loading, error } = useQuery(FUN_FACTS, {
    variables: {
      id: studentId || "",
      first: options?.first,
      last: options?.last,
      after: options?.after,
      before: options?.before,
    },
    skip: !studentId,
  });

  const { funFacts, totalCount, pageInfo } = useMemo(() => {
    if (
      !data?.node ||
      data.node.__typename !== "Student" ||
      !data.node.profileAnswers
    ) {
      return {
        funFacts: [],
        totalCount: 0,
        pageInfo: {
          startCursor: null,
          endCursor: null,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    const profileAnswers = data.node.profileAnswers;
    const funFactsArray =
      profileAnswers.edges
        ?.map((edge) => ({
          question: edge?.node?.question?.text || "",
          answer: edge?.node?.text || "",
          cursor: edge?.cursor || "",
        }))
        .filter((fact) => fact.question && fact.answer) || [];

    return {
      funFacts: funFactsArray,
      totalCount: profileAnswers.totalCount,
      pageInfo: {
        startCursor: profileAnswers.pageInfo?.startCursor,
        endCursor: profileAnswers.pageInfo?.endCursor,
        hasNextPage: profileAnswers.pageInfo?.hasNextPage || false,
        hasPreviousPage: profileAnswers.pageInfo?.hasPreviousPage || false,
      },
    };
  }, [data]);

  const getFunFactByQuestion = (questionText: string) => {
    const funFact = funFacts.find(
      (fact) => fact.question.toLowerCase() === questionText.toLowerCase(),
    );
    return funFact || null;
  };

  const hasFunFacts = funFacts.length > 0;

  return {
    funFacts,
    loading,
    error,
    totalCount,
    pageInfo,
    getFunFactByQuestion,
    hasFunFacts,
  };
};
